/**
 * Подписи — свойство СЛОЯ, а не виз-блока.
 *
 * До этого стиль подписи (`text-*` / `symbol-*`) хранился в `paint`/`layout` того блока,
 * который её рисовал. Пока блок был один, это не мешало; с двумя точечными блоками
 * (например «Точки с иконками» + «Точки») вылезало сразу три дефекта:
 *   1. панель редактировала стиль ВЕРХНЕГО label-способного блока — добавление второго
 *      блока переключало её на его пустые ключи, и настроенный стиль «слетал»;
 *   2. подписи рисовали ОБА блока — на карте две подписи с разным оформлением;
 *   3. слой подписей circle-блока (`-pt-lbl`) лежит ВЫШЕ `-sym`, а MapLibre раскладывает
 *      символы сверху вниз (`PauseablePlacement`), поэтому подпись занимала место первой
 *      и знак с иконкой вылетал из раскладки целиком — «видны только обычные точки».
 *
 * Отсюда две вещи в этом модуле: стиль подписи читается/пишется на уровне слоя
 * (`styleConfig.labelStyle`), и ровно один блок слоя назначается владельцем подписи.
 *
 * Здесь только чистые функции без MapLibre: ими пользуются редактор, вьювер и сервер.
 * Применение layout к саб-слою карты — `apps/web/src/lib/layerLabelStyle.ts`.
 */

import { CREATOR_DISPLAY_STYLE_KEY } from '../constants/creatorDisplay.js';
import type { LayerVisualization } from '../types/layer.js';

export interface LayerLabelStyle {
  paint: Record<string, unknown>;
  layout: Record<string, unknown>;
}

/**
 * Умолчания paint подписи слоя: ключа нет в стиле — подпись на карте рисуется так. Общие
 * для карты (`SYMBOL_PAINT_DEFAULTS` веба) и выгрузки планов, которая повторяет ореол.
 */
export const LAYER_LABEL_PAINT_DEFAULTS: Readonly<Record<string, unknown>> = {
  'text-color': '#000000',
  'text-opacity': 1,
  'text-halo-color': '#ffffff',
  'text-halo-width': 1,
  'text-halo-blur': 0,
};

/** Размер подписи слоя по умолчанию, px (`SYMBOL_LAYOUT_DEFAULTS` веба). */
export const LAYER_LABEL_TEXT_SIZE_DEFAULT = 14;

type StyleConfigLike = Record<string, unknown> | undefined | null;

/**
 * Ключ относится к ПОДПИСИ, а не к знаку/кружку/заливке блока.
 *
 * Только `text-*`. `symbol-*` (размещение, интервал, ключ сортировки, порядок по Z) —
 * общие для знака и текста и правятся в секции знака, а не в «Подписях»: у слоя, где
 * подпись едет в том же symbol-слое, что и иконка, зачистка их вместе с `text-*` сбивала
 * бы размещение самой иконки.
 */
export function isLabelStyleKey(key: string): boolean {
  return key.startsWith('text-');
}

/** Только label-ключи объекта; `visibility` не стиль подписи, а состояние саб-слоя. */
export function pickLabelStyleKeys(
  source: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!source || typeof source !== 'object') {
    return out;
  }
  for (const [key, value] of Object.entries(source)) {
    if (isLabelStyleKey(key) && key !== 'visibility') {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Приоритет владельца подписи среди ТОЧЕЧНЫХ блоков: symbol → sign → preview-point → circle.
 *
 * Не «верхний блок»: подпись обязана ехать в том же symbol-слое, что и иконка, иначе она
 * конкурирует с иконкой за место в раскладке MapLibre и одна из них пропадает (дефект 3
 * в шапке файла).
 *
 * `sign` (блок «Указатели») до синка карты не доходит — правило чтения раскрывает его в
 * symbol + preview-point. Но в ПАНЕЛИ слой указателей показан именно им, и текстовые стили
 * подписи хранятся в нём: без него ленивая миграция начиналась бы с пустого стиля и первая
 * же правка подписи стирала размер, отступ и ореол, настроенные у слоя указателей.
 */
const POINT_LABEL_KIND_PRIORITY = ['symbol', 'sign', 'preview-point', 'circle'] as const;

/**
 * Владелец подписи слоя: сначала блоки без `suppressText`, и только если таких нет —
 * блоки с ним.
 *
 * `suppressText` — «пусть подпись рисует кто-то другой», а не «подписей у слоя нет»:
 * поле появилось до того, как владелец стал выбираться явно, и им гасили ВТОРУЮ подпись
 * (оверлей иконок креатива, иконки помещений, превью макетов указателей). Пока это был
 * жёсткий запрет, выключение соседнего блока уносило подписи целиком: у слоя
 * «иконки + обычные точки» стоило погасить точки — и подпись исчезала с карты, а её
 * настройки пропадали из панели. Подписи же от видов визуализации не зависят.
 */
export function pickPointLabelOwnerVizId(
  styleConfig: StyleConfigLike,
  blocks: readonly LayerVisualization[],
): string | null {
  // Плашка-подпись креатива рисует текст холстом внутри маркера — symbol-блок текст не
  // ставит НИКОГДА: иначе подпись задвоилась бы (на плашке и под ней).
  const creatorLabelPlate = styleConfig?.[CREATOR_DISPLAY_STYLE_KEY] === 'label';
  const candidate = (kind: string, allowSuppressed: boolean): LayerVisualization | undefined =>
    blocks.find(
      (block) =>
        block.kind === kind &&
        block.enabled !== false &&
        (allowSuppressed || block.suppressText !== true) &&
        !(kind === 'symbol' && creatorLabelPlate),
    );
  for (const allowSuppressed of [false, true]) {
    for (const kind of POINT_LABEL_KIND_PRIORITY) {
      const owner = candidate(kind, allowSuppressed);
      if (owner) {
        return owner.id;
      }
    }
  }
  return null;
}

/** Enabled fill-блок: подписи полигонов (`-fl-lbl`) от точечного владельца не зависят. */
function findFillLabelBlock(
  blocks: readonly LayerVisualization[],
): LayerVisualization | null {
  return blocks.find((block) => block.kind === 'fill' && block.enabled !== false) ?? null;
}

/**
 * Есть ли у слоя блок, который подпись реально нарисует.
 *
 * Не «есть блок подходящего вида»: точечный блок со своим `suppressText` подписи не рисует,
 * и если других нет, панель показывала бы настройки подписи, сохраняла `labelStyle` — а на
 * карте его никто бы не прочитал.
 */
export function layerSupportsLabels(
  styleConfig: StyleConfigLike,
  blocks: readonly LayerVisualization[],
): boolean {
  return (
    pickPointLabelOwnerVizId(styleConfig, blocks) != null || findFillLabelBlock(blocks) != null
  );
}

/** Блок, из которого берётся стиль подписи при ленивой миграции (точки, иначе полигоны). */
function findLegacyLabelStyleBlock(
  styleConfig: StyleConfigLike,
  blocks: readonly LayerVisualization[],
): LayerVisualization | null {
  const ownerId = pickPointLabelOwnerVizId(styleConfig, blocks);
  const owner = ownerId ? blocks.find((block) => block.id === ownerId) : null;
  return owner ?? findFillLabelBlock(blocks);
}

/**
 * Стиль подписей слоя. Поля `labelStyle` нет (слой ещё не правился после перехода) —
 * читаем из блока-владельца, чтобы карта и панель выглядели ровно как раньше.
 */
export function readLayerLabelStyle(
  styleConfig: StyleConfigLike,
  blocks: readonly LayerVisualization[],
): LayerLabelStyle {
  const explicit = styleConfig?.labelStyle as
    | { paint?: Record<string, unknown>; layout?: Record<string, unknown> }
    | undefined;
  if (explicit && typeof explicit === 'object') {
    return {
      paint: pickLabelStyleKeys(explicit.paint),
      layout: pickLabelStyleKeys(explicit.layout),
    };
  }
  const legacy = findLegacyLabelStyleBlock(styleConfig, blocks);
  return {
    paint: pickLabelStyleKeys(legacy?.paint),
    layout: pickLabelStyleKeys(legacy?.layout),
  };
}

/**
 * paint/layout для саб-слоя подписей одного блока: явный `labelStyle` слоя перебивает
 * ключи блока целиком (иначе старые `text-*` блока «просвечивали» бы сквозь новый стиль).
 * `explicit` — знак того, что вернулся стиль слоя, а не ключи блока: symbol-слой один на
 * иконку и подпись, и ему нужно знать, какие ключи чужого блока выбрасывать.
 */
export function resolveLabelSubLayerStyle(
  styleConfig: StyleConfigLike,
  blockPaint: Record<string, unknown> | undefined,
  blockLayout: Record<string, unknown> | undefined,
): {
  paint: Record<string, unknown> | undefined;
  layout: Record<string, unknown> | undefined;
  explicit: boolean;
} {
  const explicit = styleConfig?.labelStyle as
    | { paint?: Record<string, unknown>; layout?: Record<string, unknown> }
    | undefined;
  if (!explicit || typeof explicit !== 'object') {
    return { paint: blockPaint, layout: blockLayout, explicit: false };
  }
  return {
    paint: pickLabelStyleKeys(explicit.paint),
    layout: pickLabelStyleKeys(explicit.layout),
    explicit: true,
  };
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Умолчания layout подписи, которые в слой не пишутся (правило чтения): ключ, заданный слоем,
 * важнее. Подпись читает явный `labelStyle` слоя, если он есть, иначе — ключи своего блока
 * (`resolveLabelSubLayerStyle`): у точек — блока-владельца, у полигонов — fill-блока. Поэтому
 * умолчания ложатся ровно туда, откуда их прочтут. Блоки — `styleConfig.visualizations`.
 * Добавлять нечего — та же ссылка.
 */
export function withLabelLayoutDefaults(
  styleConfig: Record<string, unknown>,
  defaults: Record<string, unknown>,
): Record<string, unknown> {
  const fill = (layout: unknown): Record<string, unknown> | null => {
    const base = isPlainRecord(layout) ? layout : {};
    const missing = Object.entries(defaults).filter(([key]) => base[key] === undefined);
    return missing.length > 0 ? { ...base, ...Object.fromEntries(missing) } : null;
  };

  const { labelStyle } = styleConfig;
  if (isPlainRecord(labelStyle)) {
    const layout = fill(labelStyle.layout);
    return layout ? { ...styleConfig, labelStyle: { ...labelStyle, layout } } : styleConfig;
  }

  const blocks = Array.isArray(styleConfig.visualizations)
    ? (styleConfig.visualizations as LayerVisualization[])
    : [];
  const ownerId = pickPointLabelOwnerVizId(styleConfig, blocks);
  const labelBlocks = new Set(
    [blocks.find((block) => block.id === ownerId), findFillLabelBlock(blocks)].filter(Boolean),
  );
  let changed = false;
  const next = blocks.map((block) => {
    const layout = labelBlocks.has(block) ? fill(block.layout) : null;
    if (!layout) {
      return block;
    }
    changed = true;
    return { ...block, layout };
  });
  return changed ? { ...styleConfig, visualizations: next } : styleConfig;
}
